import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { PlanPill } from "../components/Badges";
import { Search, Edit, MapPin, Save, Trash2, Plus, X, CreditCard, CheckCircle, AlertTriangle, Building2 } from "lucide-react";
import { toast } from "sonner";
import ImageInput from "../components/ImageInput";
import SegmentsPicker from "../components/SegmentsPicker";
import LocationPicker from "../components/LocationPicker";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCity, setFilterCity] = useState("ALL");
  const [editing, setEditing] = useState(null); // company object being edited
  const [managingSub, setManagingSub] = useState(null); // company whose subscription is being managed
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api.get("/companies?include_inactive=true").then((r) => setCompanies(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return companies.filter((c) => {
      if (filterCity !== "ALL" && c.city_slug !== filterCity) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!c.name.toLowerCase().includes(s) && !(c.category || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [companies, search, filterCity]);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = {
        name: editing.name,
        description: editing.description,
        category: editing.category,
        city_slug: editing.city_slug,
        address: editing.address,
        lat: parseFloat(editing.lat),
        lng: parseFloat(editing.lng),
        phone: editing.phone || null,
        whatsapp: editing.whatsapp || null,
        instagram: editing.instagram || null,
        website: editing.website || null,
        accepts_cashback: !!editing.accepts_cashback,
        cashback_rate_pct: parseFloat(editing.cashback_rate_pct ?? 5),
        logo_url: editing.logo_url || null,
        cover_url: editing.cover_url || null,
        segments: editing.segments || [],
        media: editing.media || [],
        active: editing.active !== false,
        pix_key: editing.pix_key || null,
        cnpj: editing.cnpj ? String(editing.cnpj).replace(/[^0-9]/g, "") : null,
        has_delivery: !!editing.has_delivery,
        pickup_available: editing.pickup_available !== false,
        business_hours: editing.business_hours || null,
      };
      await api.patch(`/companies/${editing.id}`, payload);
      toast.success("Empresa atualizada");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/companies/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Empresa inativada" : "Empresa ativada");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Empresas — Administração</h1>
          <p className="text-stone-500 text-sm mt-1">Controle total sobre dados, localização, planos e mídias.</p>
        </div>
        <a href="/admin" className="text-sm text-blue-900 hover:text-blue-950">← Voltar ao painel</a>
      </div>

      <Card className="p-4 rounded-2xl border-stone-200 mb-6 flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input placeholder="Buscar por nome ou categoria..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-xl border-stone-200" data-testid="admin-companies-search"/>
        </div>
        <Select value={filterCity} onValueChange={setFilterCity}>
          <SelectTrigger className="w-48 rounded-xl" data-testid="admin-companies-city"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas cidades</SelectItem>
            {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-stone-500">{filtered.length} resultado(s)</span>
      </Card>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Empresa</th>
                <th className="text-left px-4 py-3">Cidade</th>
                <th className="text-left px-4 py-3">Categoria</th>
                <th className="text-left px-4 py-3">Assinatura</th>
                <th className="text-left px-4 py-3">Coords</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`admin-company-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {c.logo_url ? (
                        <img src={c.logo_url} alt={c.name} className="w-8 h-8 rounded-lg object-contain bg-white border border-stone-200 p-0.5"/>
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-stone-100 text-stone-400 flex items-center justify-center font-display font-bold">{c.name[0]}</div>
                      )}
                      <div>
                        <div className="font-medium text-stone-900">{c.name}</div>
                        <div className="text-[11px] text-stone-500">{c.address}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-600">{c.city_slug}</td>
                  <td className="px-4 py-3 text-stone-600">{c.category}</td>
                  <td className="px-4 py-3"><SubscriptionCell company={c}/></td>
                  <td className="px-4 py-3 text-[11px] text-stone-500 font-mono">{c.lat.toFixed(4)}, {c.lng.toFixed(4)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => setManagingSub(c)} className="rounded-lg mr-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`manage-sub-${c.id}`}>
                      <CreditCard size={13} className="mr-1"/> Assinatura
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...c })} className="rounded-lg" data-testid={`edit-company-${c.id}`}>
                      <Edit size={13} className="mr-1"/> Editar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && <CompanyEditDialog company={editing} setCompany={setEditing} cities={cities} onSave={save} onClose={() => setEditing(null)} busy={busy} />}
      {managingSub && <SubscriptionManagementDialog company={managingSub} onClose={() => { setManagingSub(null); refresh(); }} />}
    </div>
  );
}

function SubscriptionCell({ company }) {
  const plan = company.plan || "GRATIS";
  const expires = company.plan_expires_at ? new Date(company.plan_expires_at) : null;
  const now = new Date();
  const isActive = plan !== "GRATIS" && expires && expires > now;
  const isExpired = plan !== "GRATIS" && expires && expires <= now;
  return (
    <div className="flex flex-col gap-1">
      <PlanPill plan={plan}/>
      {isActive && (
        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-bold">
          <CheckCircle size={10}/> ATIVA · até {expires.toLocaleDateString("pt-BR")}
        </span>
      )}
      {isExpired && (
        <span className="inline-flex items-center gap-1 text-[10px] text-red-600 font-bold">
          <AlertTriangle size={10}/> VENCIDA em {expires.toLocaleDateString("pt-BR")}
        </span>
      )}
      {plan === "GRATIS" && (
        <span className="text-[10px] text-stone-400">Plano gratuito</span>
      )}
    </div>
  );
}

function SubscriptionManagementDialog({ company, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planTier, setPlanTier] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/companies/${company.id}/subscription`);
      setData(r.data);
      setPlanTier(r.data.plan || "GRATIS");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyManualPlan = async () => {
    if (!planTier) return;
    if (!window.confirm(`Confirmar ativação manual do plano ${planTier} para ${company.name}?`)) return;
    setSaving(true);
    try {
      const body = { plan_tier: planTier, reason };
      if (durationDays) body.duration_days = parseInt(durationDays, 10);
      await api.post(`/admin/companies/${company.id}/set-plan`, body);
      toast.success("Plano atualizado");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const recheckMP = async () => {
    setChecking(true);
    try {
      // Use the merchant-side endpoint but admin can also call (we'd need to add admin variant)
      // For now, just refresh - admin can use simulate or manual set
      await load();
      toast.success("Atualizado");
    } finally { setChecking(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Gestão de Assinatura — {company.name}</DialogTitle>
        </DialogHeader>
        {loading && <div className="py-12 text-center text-stone-500">Carregando...</div>}
        {!loading && data && (
          <div className="space-y-5">
            <Card className="p-4 rounded-xl bg-stone-50 border-stone-200">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">Status atual</div>
                  <div className="flex items-center gap-3">
                    <PlanPill plan={data.plan}/>
                    <span className={`text-xs font-bold ${data.subscription_status === "ATIVA" ? "text-emerald-700" : data.subscription_status === "VENCIDA" ? "text-red-600" : "text-stone-400"}`}>
                      {data.subscription_status}
                    </span>
                  </div>
                  {data.plan_expires_at && (
                    <div className="text-xs text-stone-500 mt-1">Expira em: {new Date(data.plan_expires_at).toLocaleString("pt-BR")}</div>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={recheckMP} disabled={checking} className="rounded-lg" data-testid="recheck-sub-btn">
                  Atualizar
                </Button>
              </div>
            </Card>

            <Card className="p-4 rounded-xl border-blue-200 bg-blue-50">
              <h3 className="font-display text-base font-bold text-blue-900 mb-3">Override Manual</h3>
              <p className="text-xs text-blue-800 mb-3">Use quando o pagamento foi confirmado offline ou por outro canal (PIX direto, transferência, etc).</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Plano</Label>
                  <Select value={planTier} onValueChange={setPlanTier}>
                    <SelectTrigger className="rounded-xl mt-1 bg-white" data-testid="sub-plan-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GRATIS">GRATIS (rebaixar)</SelectItem>
                      <SelectItem value="BRONZE">BRONZE</SelectItem>
                      <SelectItem value="PRATA">PRATA</SelectItem>
                      <SelectItem value="OURO">OURO</SelectItem>
                      <SelectItem value="PLATINUM">PLATINUM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Duração (dias)</Label>
                  <Input type="number" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} placeholder="30 (default)" className="rounded-xl mt-1 bg-white" data-testid="sub-duration-input"/>
                </div>
                <div>
                  <Label className="text-xs">Motivo / observação</Label>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: pagamento via PIX" className="rounded-xl mt-1 bg-white" data-testid="sub-reason-input"/>
                </div>
              </div>
              <Button onClick={applyManualPlan} disabled={saving || !planTier} className="mt-3 bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="apply-manual-plan-btn">
                {saving ? "Salvando..." : "Aplicar manualmente"}
              </Button>
            </Card>

            <div>
              <h3 className="font-display text-base font-bold text-stone-900 mb-2">Histórico de pagamentos aplicados</h3>
              {data.payments?.length === 0 && <div className="text-xs text-stone-400 italic">Nenhum pagamento registrado.</div>}
              {data.payments?.length > 0 && (
                <div className="rounded-xl border border-stone-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 text-stone-600">
                      <tr>
                        <th className="text-left px-3 py-2">Data</th>
                        <th className="text-left px-3 py-2">Plano</th>
                        <th className="text-left px-3 py-2">MP ID / Fonte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.payments.map((p) => (
                        <tr key={p.id} className="border-t border-stone-100">
                          <td className="px-3 py-2 text-stone-600">{new Date(p.applied_at).toLocaleString("pt-BR")}</td>
                          <td className="px-3 py-2"><PlanPill plan={p.plan_tier}/></td>
                          <td className="px-3 py-2 font-mono text-[10px] text-stone-500">{p.source === "admin_manual" ? `MANUAL · ${p.reason || "-"}` : p.mp_payment_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-display text-base font-bold text-stone-900 mb-2">Preferências Mercado Pago</h3>
              {data.preferences?.length === 0 && <div className="text-xs text-stone-400 italic">Nenhuma preferência criada.</div>}
              {data.preferences?.length > 0 && (
                <div className="rounded-xl border border-stone-200 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 text-stone-600">
                      <tr>
                        <th className="text-left px-3 py-2">Data</th>
                        <th className="text-left px-3 py-2">Plano</th>
                        <th className="text-left px-3 py-2">Valor</th>
                        <th className="text-left px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.preferences.map((p) => (
                        <tr key={p.id} className="border-t border-stone-100">
                          <td className="px-3 py-2 text-stone-600">{new Date(p.created_at).toLocaleString("pt-BR")}</td>
                          <td className="px-3 py-2">{p.plan_tier}</td>
                          <td className="px-3 py-2">R$ {(p.amount || 0).toFixed(2)}</td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] font-bold ${p.status === "paid" ? "text-emerald-700" : "text-amber-600"}`}>
                              {p.status === "paid" ? "PAGO" : "PENDENTE"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CompanyEditDialog({ company, setCompany, cities, onSave, onClose, busy, isAdmin = true }) {
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/company-categories?active=true").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);
  // Decora `branches` injetando flags `*_same` (true quando o campo é null no backend = herda da matriz)
  useEffect(() => {
    if (!company || company.__branches_decorated) return;
    const decorated = (company.branches || []).map((b) => ({
      ...b,
      phone_same: b.phone == null,
      whatsapp_same: b.whatsapp == null,
      instagram_same: b.instagram == null,
      business_hours_same: b.business_hours == null,
    }));
    setCompany({ ...company, branches: decorated, __branches_decorated: true });
  }, [company?.id]);

  const update = (patch) => setCompany({ ...company, ...patch });
  const updateMedia = (idx, patch) => {
    const next = [...(company.media || [])];
    next[idx] = { ...next[idx], ...patch };
    update({ media: next });
  };
  const addMedia = (kind) => update({ media: [...(company.media || []), { url: "", kind, caption: "" }] });
  const removeMedia = (idx) => update({ media: (company.media || []).filter((_, i) => i !== idx) });

  // Filiais
  const addBranch = () => update({ branches: [...(company.branches || []), {
    name: "", address: "", lat: company.lat || 0, lng: company.lng || 0,
    phone: "", whatsapp: "", instagram: "", business_hours: "",
    phone_same: true, whatsapp_same: true, instagram_same: true, business_hours_same: true,
    active: true,
  }] });
  const updateBranch = (idx, patch) => {
    const next = [...(company.branches || [])];
    next[idx] = { ...next[idx], ...patch };
    update({ branches: next });
  };
  const removeBranch = (idx) => update({ branches: (company.branches || []).filter((_, i) => i !== idx) });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">Editar empresa</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 flex items-center gap-3">
            {company.logo_url ? (
              <img src={company.logo_url} alt="logo" className="w-16 h-16 rounded-xl object-contain bg-white border border-stone-200 p-1"/>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-stone-100 text-stone-300 flex items-center justify-center font-display font-bold text-2xl">{(company.name || "?")[0]}</div>
            )}
            <div className="flex-1">
              <Label>Logo (URL ou upload)</Label>
              <div className="mt-1"><ImageInput value={company.logo_url || ""} onChange={(v) => update({ logo_url: v })} aspect="1/1" testid="ef-logo" showPreview={false}/></div>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Imagem de capa (16:8, aparece nos destaques da home e no popup do mapa)</Label>
            <div className="mt-1"><ImageInput value={company.cover_url || ""} onChange={(v) => update({ cover_url: v })} aspect="16/8" testid="ef-cover"/></div>
          </div>
          <div className="sm:col-span-2"><SegmentsPicker value={company.segments || []} onChange={(v) => update({ segments: v })} testid="ef-segments"/></div>
          <div><Label>Nome</Label><Input value={company.name || ""} onChange={(e) => update({ name: e.target.value })} className="rounded-xl mt-1" data-testid="ef-name"/></div>
          <div><Label>Categoria</Label>
            <Select value={company.category || ""} onValueChange={(v) => update({ category: v })}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="ef-category"><SelectValue placeholder="Selecione"/></SelectTrigger>
              <SelectContent className="max-h-72">
                {categories.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label>Descrição</Label><Input value={company.description || ""} onChange={(e) => update({ description: e.target.value })} className="rounded-xl mt-1" data-testid="ef-description"/></div>
          <div className="sm:col-span-2"><Label>Endereço</Label><Input value={company.address || ""} onChange={(e) => update({ address: e.target.value })} className="rounded-xl mt-1" data-testid="ef-address"/></div>
          <div>
            <Label>Cidade</Label>
            <Select value={company.city_slug} onValueChange={(v) => update({ city_slug: v })}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="ef-city"><SelectValue/></SelectTrigger>
              <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-stone-500">Plano</Label>
            <div className="mt-1 p-2.5 rounded-xl bg-stone-50 border border-stone-200 flex items-center gap-2">
              <PlanPill plan={company.plan || "GRATIS"}/>
              <span className="text-[11px] text-stone-500">— alterar via Gestão de Assinatura</span>
            </div>
          </div>
          <div><Label>CNPJ</Label><Input value={company.cnpj || ""} onChange={(e) => update({ cnpj: e.target.value })} className="rounded-xl mt-1 font-mono" placeholder="00.000.000/0001-00" data-testid="ef-cnpj"/></div>
          <div><Label>Chave PIX (repasse de vendas)</Label><Input value={company.pix_key || ""} onChange={(e) => update({ pix_key: e.target.value })} className="rounded-xl mt-1" placeholder="CPF/CNPJ, email, telefone ou chave aleatória" data-testid="ef-pix"/></div>
          <div><Label>Vigência padrão do voucher (dias)</Label><Input type="number" min="1" value={company.voucher_default_validity_days ?? 30} onChange={(e) => update({ voucher_default_validity_days: parseInt(e.target.value) || 30 })} className="rounded-xl mt-1 w-32" data-testid="ef-voucher-validity"/></div>
          {isAdmin ? (
            <div className="flex items-center gap-2 mt-6"><Switch checked={company.active !== false} onCheckedChange={(v) => update({ active: v })} data-testid="ef-active"/><Label>Empresa ativa (aparece nas buscas)</Label></div>
          ) : (
            <div className="mt-6">
              <Label className="text-stone-500 text-xs">Status</Label>
              <div className="mt-1 p-2.5 rounded-xl bg-stone-50 border border-stone-200 text-sm">
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${company.active !== false ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${company.active !== false ? "bg-emerald-600" : "bg-red-600"}`}></span>
                  {company.active !== false ? "Ativa" : "Inativa"}
                </span>
                <p className="text-[11px] text-stone-500 mt-1.5">Status definido pelo Administrador.</p>
              </div>
            </div>
          )}
          {isAdmin ? (
            <div className="sm:col-span-2 grid grid-cols-2 gap-3 p-3 rounded-xl bg-blue-50 border border-blue-200">
              <div className="col-span-2">
                <Label className="text-blue-950 font-semibold">Cashback dinâmico da empresa</Label>
                <p className="text-[11px] text-stone-600 mt-0.5">Percentual concedido sobre cada compra. A empresa paga via Mercado Pago + taxa da plataforma.</p>
              </div>
              <div>
                <Label>Taxa de cashback (%)</Label>
                <Input type="number" step="0.1" min="0" value={company.cashback_rate_pct ?? 5} onChange={(e) => update({ cashback_rate_pct: parseFloat(e.target.value) })} className="rounded-xl mt-1" data-testid="ef-cashback-rate"/>
              </div>
              <div className="flex items-center gap-2 mt-6"><Switch checked={!!company.accepts_cashback} onCheckedChange={(v) => update({ accepts_cashback: v })} data-testid="ef-cashback"/><Label>Aceita cashback (Prata/Ouro)</Label></div>
            </div>
          ) : (
            <div className="sm:col-span-2 p-3 rounded-xl bg-blue-50/40 border border-blue-200">
              <Label className="text-blue-950 font-semibold text-sm">Cashback dinâmico da empresa</Label>
              <p className="text-[11px] text-stone-600 mt-0.5 mb-2">Parametrização gerenciada pelo Administrador da plataforma.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Taxa de cashback</div>
                  <div className="font-display text-xl font-bold text-blue-950">{Number(company.cashback_rate_pct ?? 5).toFixed(1)}%</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">Aceita cashback</div>
                  <div className="mt-0.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${company.accepts_cashback ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-600"}`}>
                      {company.accepts_cashback ? "Sim" : "Não"}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-stone-500 mt-2">💡 Para alterar, entre em contato com o suporte ou seu gerente de conta.</p>
            </div>
          )}
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl bg-emerald-50/40 border border-emerald-200">
            <div className="sm:col-span-2">
              <Label className="text-emerald-900 font-semibold text-sm">Operação · Entrega e Atendimento</Label>
              <p className="text-[11px] text-stone-600 mt-0.5">Define se sua loja oferece delivery e o horário de funcionamento — aparece pro cliente no card e na página da empresa.</p>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-white border border-stone-200">
              <Switch checked={!!company.has_delivery} onCheckedChange={(v) => update({ has_delivery: v })} data-testid="ef-has-delivery"/>
              <Label className="text-sm">Faz entrega (delivery)</Label>
            </div>
            <div className="flex items-center gap-2 p-2 rounded-lg bg-white border border-stone-200">
              <Switch checked={company.pickup_available !== false} onCheckedChange={(v) => update({ pickup_available: v })} data-testid="ef-pickup"/>
              <Label className="text-sm">Retirada em loja disponível</Label>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Horário de funcionamento</Label>
              <Input value={company.business_hours || ""} onChange={(e) => update({ business_hours: e.target.value })} placeholder="Ex.: Seg-Sex 09h-18h | Sáb 09h-13h" className="rounded-xl mt-1" data-testid="ef-hours"/>
            </div>
          </div>
          <div className="sm:col-span-2 grid grid-cols-2 gap-3">
            <div><Label>Latitude <span className="text-blue-900"><MapPin size={11} className="inline"/></span></Label><Input value={company.lat} onChange={(e) => update({ lat: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="ef-lat"/></div>
            <div><Label>Longitude</Label><Input value={company.lng} onChange={(e) => update({ lng: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="ef-lng"/></div>
          </div>
          <div className="sm:col-span-2">
            <LocationPicker
              value={{ lat: parseFloat(company.lat) || 0, lng: parseFloat(company.lng) || 0 }}
              onChange={(p) => update({ lat: p.lat.toFixed(6), lng: p.lng.toFixed(6), address: p.address || company.address })}
              label="📍 Selecionar localização no mapa"
              buttonClass="w-full bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
            />
          </div>
          <div><Label>Telefone</Label><Input value={company.phone || ""} onChange={(e) => update({ phone: e.target.value })} className="rounded-xl mt-1"/></div>
          <div><Label>WhatsApp</Label><Input value={company.whatsapp || ""} onChange={(e) => update({ whatsapp: e.target.value })} className="rounded-xl mt-1"/></div>
          <div><Label>Instagram</Label><Input value={company.instagram || ""} onChange={(e) => update({ instagram: e.target.value })} className="rounded-xl mt-1"/></div>
          <div><Label>Website</Label><Input value={company.website || ""} onChange={(e) => update({ website: e.target.value })} className="rounded-xl mt-1"/></div>

          {/* ====== FILIAIS ====== */}
          <div className="sm:col-span-2 mt-2 p-4 rounded-xl border border-blue-200 bg-blue-50/40 space-y-3" data-testid="ef-branches">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <Label className="text-base flex items-center gap-1.5 text-blue-900"><Building2 size={15}/> Filiais físicas</Label>
                <p className="text-[11px] text-stone-600 mt-0.5">Aparecem como marcadores adicionais no mapa. A lista do Descobrir continua mostrando 1 card por matriz.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addBranch} className="rounded-lg" data-testid="ef-branch-add"><Plus size={13} className="mr-1"/> Adicionar filial</Button>
            </div>
            {(company.branches || []).length === 0 ? (
              <p className="text-xs text-stone-500 italic">Nenhuma filial cadastrada. Esta empresa só aparece em um ponto do mapa.</p>
            ) : (
              <div className="space-y-3">
                {(company.branches || []).map((b, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white border border-stone-200 space-y-2" data-testid={`ef-branch-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Input value={b.name || ""} onChange={(e) => updateBranch(i, { name: e.target.value })} placeholder={`Filial ${i + 1}`} className="rounded-lg h-9 text-sm font-semibold flex-1" data-testid={`ef-branch-name-${i}`}/>
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-stone-50 border border-stone-200">
                        <Switch checked={b.active !== false} onCheckedChange={(v) => updateBranch(i, { active: v })} data-testid={`ef-branch-active-${i}`}/>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-stone-600">Ativa</span>
                      </div>
                      <button type="button" onClick={() => removeBranch(i)} className="p-1.5 rounded text-red-500 hover:bg-red-50" data-testid={`ef-branch-remove-${i}`}><Trash2 size={14}/></button>
                    </div>
                    <Input value={b.address || ""} onChange={(e) => updateBranch(i, { address: e.target.value })} placeholder="Endereço completo" className="rounded-lg h-9 text-sm" data-testid={`ef-branch-address-${i}`}/>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={b.lat ?? ""} onChange={(e) => updateBranch(i, { lat: e.target.value })} placeholder="Latitude" className="rounded-lg h-9 text-xs font-mono" data-testid={`ef-branch-lat-${i}`}/>
                      <Input value={b.lng ?? ""} onChange={(e) => updateBranch(i, { lng: e.target.value })} placeholder="Longitude" className="rounded-lg h-9 text-xs font-mono" data-testid={`ef-branch-lng-${i}`}/>
                    </div>
                    <LocationPicker
                      value={{ lat: parseFloat(b.lat) || 0, lng: parseFloat(b.lng) || 0 }}
                      onChange={(p) => updateBranch(i, { lat: p.lat.toFixed(6), lng: p.lng.toFixed(6), address: p.address || b.address })}
                      label="📍 Selecionar no mapa"
                      buttonClass="w-full h-8 text-xs bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                    />
                    <div className="pt-1 grid sm:grid-cols-2 gap-2">
                      {[
                        ["phone", "Telefone", company.phone],
                        ["whatsapp", "WhatsApp", company.whatsapp],
                        ["instagram", "Instagram", company.instagram],
                        ["business_hours", "Horário", company.business_hours],
                      ].map(([field, label, fallback]) => (
                        <div key={field} className="p-2 rounded-lg bg-stone-50 border border-stone-200 space-y-1">
                          <div className="flex items-center justify-between">
                            <Label className="text-[11px] font-semibold">{label}</Label>
                            <label className="flex items-center gap-1.5 text-[10px] text-stone-600 cursor-pointer select-none">
                              <Switch checked={!!b[`${field}_same`]} onCheckedChange={(v) => updateBranch(i, { [`${field}_same`]: v })} data-testid={`ef-branch-${field}-same-${i}`}/>
                              Igual à matriz
                            </label>
                          </div>
                          {b[`${field}_same`] ? (
                            <p className="text-[11px] text-stone-500 italic px-1 py-1 truncate">{fallback || <span className="text-stone-400">— matriz sem {label.toLowerCase()} —</span>}</p>
                          ) : (
                            <Input value={b[field] || ""} onChange={(e) => updateBranch(i, { [field]: e.target.value })} placeholder={label} className="rounded-md h-8 text-xs" data-testid={`ef-branch-${field}-${i}`}/>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sm:col-span-2 mt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base">Galeria (mídias)</Label>
              <div className="flex gap-1.5">
                <Button type="button" size="sm" variant="outline" onClick={() => addMedia("image")} className="rounded-lg" data-testid="ef-add-image"><Plus size={12} className="mr-1"/> Imagem</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => addMedia("video")} className="rounded-lg" data-testid="ef-add-video"><Plus size={12} className="mr-1"/> Vídeo</Button>
              </div>
            </div>
            <div className="space-y-2">
              {(company.media || []).length === 0 && <p className="text-xs text-stone-400">Nenhuma mídia. Adicione URLs de fotos/vídeos.</p>}
              {(company.media || []).map((m, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-xl border border-stone-200 bg-stone-50" data-testid={`ef-media-${i}`}>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-stone-200 text-stone-700">{m.kind}</span>
                  <Input value={m.url} onChange={(e) => updateMedia(i, { url: e.target.value })} placeholder="URL" className="rounded-lg flex-1"/>
                  <Input value={m.caption || ""} onChange={(e) => updateMedia(i, { caption: e.target.value })} placeholder="Legenda" className="rounded-lg w-40 hidden sm:block"/>
                  <button onClick={() => removeMedia(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={onSave} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white" data-testid="ef-save"><Save size={14} className="mr-1.5"/>{busy ? "Salvando..." : "Salvar alterações"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
